import { openBullionLiveByUserId } from "./leagueBullionProjectedPoints";
import { LEAGUE_GAME_BAR_COLORS } from "./constants";
import { leagueIncludedGames } from "./leagueFormat";
import {
    leaguePlayChipReturn,
    leagueSquaresOpenLiveReturn,
    leagueTraderWonPoints,
    potentialReturn,
} from "./leaguePlays";
import type { League, LeagueGameKey, LeaguePlay, LeagueSettlement, LeagueTableRow } from "./types";

export type LeagueLeaderboardGameBarSlice = {
    game: LeagueGameKey;
    /**
     * Points on the table: settled plays plus live Sixes / Squares+ / Bullion pick score.
     * Open Bullion place bonus is not included (that lives on IF NOW until settle).
     */
    won: number;
    /** Chips still available from open plays (potential return). */
    pot: number;
    color: string;
    /** Open Bullion slip score — Bullion tab bar / PTS. Omitted when the battle is not open for this user. */
    livePts?: number;
    /** If-the-battle-ended-now standings (slip + place bonus). Null until any slip has scored. */
    ifNowPts?: number | null;
};

export type LeagueLeaderboardBarStats = {
    games: LeagueLeaderboardGameBarSlice[];
    /** Total won points across games. Open Bullion pick score is included; place bonus is not. */
    wonPoints: number;
    /** Ceiling if every open play wins (= won + remaining pot). */
    potPoints: number;
    /** Remaining unsettled potential only. */
    remainingPot: number;
    /**
     * Open Bullion projected standings (slip + place) on the Bullion tab.
     * Null on Overall and when projection has not started.
     */
    ifNowPoints: number | null;
};

function emptySlices(games: LeagueGameKey[]): Record<LeagueGameKey, { won: number; pot: number }> {
    const out = {} as Record<LeagueGameKey, { won: number; pot: number }>;
    for (const game of games) out[game] = { won: 0, pot: 0 };
    return out;
}

function playReturn(play: Pick<LeaguePlay, "stake" | "delta" | "outcome" | "marketDetails">): number {
    return leaguePlayChipReturn(play);
}

function settlementReturn(
    row: Pick<LeagueSettlement, "stake" | "delta" | "outcome" | "game" | "marketDetails">,
): number {
    // Squares: every over's chip return counts toward league points (not only profitable wins).
    if (row.game === "squares") return leaguePlayChipReturn(row);
    if (row.game === "trader") return leagueTraderWonPoints(row);
    if (row.outcome !== "win" && !(Number(row.delta) > 0)) return 0;
    return leaguePlayChipReturn(row);
}

/**
 * Per-game won / pot totals for one leaderboard row.
 * Won = settled points (Squares: every over return; Trader: stake×odds return; Bullion: slip +
 * place bonus after settle; Sixes: native score) plus live Sixes / Squares+ / Bullion pick score.
 * Open Bullion place bonus is IF NOW on the Bullion tab only — not Overall.
 * Falls back to positive by-game P/L when the prototype seeded stats without matching plays.
 */
export function leagueLeaderboardBarStatsForMember(
    league: League,
    userId: number,
    games: LeagueGameKey[] = leagueIncludedGames(league),
): LeagueLeaderboardBarStats {
    const buckets = emptySlices(games);
    const gameSet = new Set(games);
    /** Games this user has any play on — real play data wins over the seeded byGame fallback. */
    const playedGames = new Set<LeagueGameKey>();
    const openBullionLive = games.includes("bullion") ? openBullionLiveByUserId(league) : null;
    const openBullionRow = openBullionLive?.get(userId) ?? null;

    for (const play of league.plays) {
        if (play.userId !== userId || !gameSet.has(play.game)) continue;
        playedGames.add(play.game);

        // Native in-play points per game (unified "points" leaderboard):
        // Bullion open → live pick score on Overall; if-now place bonus on the Bullion tab;
        // Bullion settled → slip + place bonus; Sixes → live card score;
        // Squares open → Super return from balls; Trader → coins.
        const settlement = play.marketDetails?.settlement;
        const bullionPoolShare =
            play.game === "bullion" &&
            play.status !== "open" &&
            settlement &&
            Number.isFinite(Number(settlement.points))
                ? Math.max(0, Math.round(Number(settlement.points) || 0))
                : null;
        const sixesScore =
            play.game === "sixes" && Number.isFinite(Number(play.marketDetails?.cardScore))
                ? Math.max(0, Math.round(Number(play.marketDetails!.cardScore) || 0))
                : null;
        const openSquaresLive =
            play.game === "squares" && play.status === "open" ? leagueSquaresOpenLiveReturn(play) : null;

        if (play.status === "open") {
            if (play.game === "bullion") {
                continue;
            } else if (play.game === "sixes") {
                // Sixes has no pot ceiling — the live card score is WON only and only grows.
                if (sixesScore != null) buckets.sixes.won += sixesScore;
            } else if (play.game === "squares") {
                // Super Squares: live chip return counts as WON (same as settled over return).
                if (openSquaresLive != null) buckets.squares.won += openSquaresLive;
            } else {
                // Trader (and any other stake×odds game): open potential = full return.
                buckets[play.game].pot += potentialReturn(play.stake, play.multiplier);
            }
            continue;
        }

        // Settled: Bullion uses slip + place bonus; Sixes keeps native score;
        // Squares counts each over's chip return; Trader counts full stake×odds return on a win.
        if (bullionPoolShare != null) {
            buckets[play.game].won += bullionPoolShare;
        } else if (sixesScore != null) {
            buckets.sixes.won += sixesScore;
        } else if (play.game === "squares") {
            buckets.squares.won += playReturn(play);
        } else if (play.game === "trader") {
            buckets.trader.won += leagueTraderWonPoints(play);
        } else if (play.outcome === "win" || (play.delta != null && play.delta > 0)) {
            buckets[play.game].won += playReturn(play);
        }
    }

    for (const row of league.settlements) {
        if (row.userId !== userId || !gameSet.has(row.game)) continue;
        // Prefer settled play rows when present for this user+game; settlements cover seed gaps.
        const hasSettledPlay = league.plays.some(
            (p) => p.userId === userId && p.game === row.game && p.status === "settled",
        );
        if (hasSettledPlay) continue;
        buckets[row.game].won += settlementReturn(row);
    }

    const member = league.members.find((m) => m.userId === userId);
    if (member) {
        for (const game of games) {
            // Native-score games and Trader (stake×odds return) must not be clobbered by byGame P/L,
            // which is net coins (wins minus losses) rather than pts won.
            if (
                (game === "bullion" || game === "sixes" || game === "trader" || game === "squares") &&
                playedGames.has(game)
            ) {
                continue;
            }
            const stats = member.byGame[game];
            if (!stats || stats.pl <= 0) continue;
            // Seeded boards often have byGame P/L without a full settled-play history.
            buckets[game].won = Math.max(buckets[game].won, Math.round(stats.pl));
        }
    }

    const slices: LeagueLeaderboardGameBarSlice[] = games.map((game) => {
        const slice: LeagueLeaderboardGameBarSlice = {
            game,
            won: Math.max(0, Math.round(buckets[game].won)),
            pot: Math.max(0, Math.round(buckets[game].pot)),
            color: LEAGUE_GAME_BAR_COLORS[game],
        };
        if (game === "bullion" && openBullionRow) {
            const slip = Math.max(0, Math.round(openBullionRow.slip));
            slice.livePts = slip;
            slice.ifNowPts = openBullionRow.ifNow;
            slice.won += slip;
        }
        return slice;
    });

    const wonPoints = slices.reduce((sum, s) => sum + s.won, 0);
    const remainingPot = slices.reduce((sum, s) => sum + s.pot, 0);
    return {
        games: slices,
        wonPoints,
        remainingPot,
        potPoints: wonPoints + remainingPot,
        ifNowPoints: null,
    };
}

/** Shared bar scale: largest won-points ceiling among visible rows. */
export function leagueLeaderboardBarScaleMax(statsList: readonly LeagueLeaderboardBarStats[]): number {
    let max = 1;
    for (const stats of statsList) {
        max = Math.max(max, stats.wonPoints);
    }
    return max;
}

export function leagueLeaderboardBarStatsByUserId(
    league: League,
    rows: readonly LeagueTableRow[],
): Map<number, LeagueLeaderboardBarStats> {
    const games = leagueIncludedGames(league);
    const map = new Map<number, LeagueLeaderboardBarStats>();
    for (const row of rows) {
        map.set(row.userId, leagueLeaderboardBarStatsForMember(league, row.userId, games));
    }
    return map;
}

/** Restrict stats to a single game tab (same colours, only that slice). */
export function leagueLeaderboardBarStatsForGame(
    stats: LeagueLeaderboardBarStats,
    game: LeagueGameKey,
): LeagueLeaderboardBarStats {
    const slice = stats.games.find((g) => g.game === game) ?? {
        game,
        won: 0,
        pot: 0,
        color: LEAGUE_GAME_BAR_COLORS[game],
    };
    if (game === "bullion" && slice.livePts != null) {
        const live = Math.max(0, Math.round(slice.livePts));
        return {
            games: [{ ...slice, won: live, pot: 0 }],
            wonPoints: live,
            remainingPot: 0,
            potPoints: live,
            ifNowPoints: slice.ifNowPts ?? null,
        };
    }
    return {
        games: [slice],
        wonPoints: slice.won,
        remainingPot: slice.pot,
        potPoints: slice.won + slice.pot,
        ifNowPoints: null,
    };
}

export function leagueLeaderboardBarHasSignal(stats: LeagueLeaderboardBarStats): boolean {
    return stats.wonPoints > 0;
}

/**
 * Viewer's overall standing by WON points — same basis as the Overall leaderboard.
 */
export function leagueLiveOverallStanding(
    league: League,
    userId: number,
): { rank: number; wonPoints: number; potPoints: number } | null {
    if (!league.members.some((m) => m.userId === userId)) return null;

    const scored = league.members.map((member) => {
        const stats = leagueLeaderboardBarStatsForMember(league, member.userId);
        return {
            userId: member.userId,
            playerName: member.playerName,
            won: stats.wonPoints,
            pot: stats.potPoints,
        };
    });
    scored.sort((a, b) => {
        if (b.won !== a.won) return b.won - a.won;
        if (b.pot !== a.pot) return b.pot - a.pot;
        return a.playerName.localeCompare(b.playerName);
    });

    let lastWon: number | null = null;
    let lastRank = 0;
    for (let index = 0; index < scored.length; index += 1) {
        const entry = scored[index]!;
        let rank: number;
        if (lastWon != null && entry.won === lastWon) {
            rank = lastRank;
        } else {
            rank = index + 1;
            lastRank = rank;
            lastWon = entry.won;
        }
        if (entry.userId === userId) {
            return { rank, wonPoints: entry.won, potPoints: entry.pot };
        }
    }
    return null;
}

/** Same order as play / leaderboard tabs (only games in the included set). */
export function leagueLeaderboardBarGames(league: Pick<League, "includedGames">): LeagueGameKey[] {
    return leagueIncludedGames(league);
}
